import { motion } from "framer-motion";
import sampradayData from "~/data/about-us/sampraday.json";
import { useFontStyle } from "~/hook/useFontStyle";
import useIntersectionObserver from "~/hook/useIntersectionObserver";
import { cn } from "~/lib/utils";

// Hero Section with Title
const HeroSection = () => {
  const [ref, isVisible] = useIntersectionObserver();
  const { medium } = useFontStyle();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.7, ease: "easeOut" },
    },
  };

  return (
    <motion.section
      ref={ref}
      initial="hidden"
      animate={isVisible ? "visible" : "hidden"}
      variants={containerVariants}
      className="relative w-full mb-5"
    >
      <div className="w-full flex justify-center items-center px-4 md:px-6">
        <div className="w-full max-w-5xl">
          <motion.div variants={itemVariants} className="text-center mb-8">
            <h1 className={cn("text-3xl md:text-5xl font-bold text-textColor mb-4", medium)}>Swaminarayan Sampradaya</h1>
            <div className="w-24 h-1 bg-gradient-to-r from-transparent via-primaryColor to-transparent mx-auto" />
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
};

// Introduction Section with Timeline Style
const IntroductionSection = () => {
  const [ref, isVisible] = useIntersectionObserver();
  const { normalInter } = useFontStyle();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2,
      },
    },
  };

  const textVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  // Split introduction into meaningful paragraphs
  const introParts = sampradayData.content.introduction.split(". ");
  const firstPart = introParts.slice(0, 3).join(". ") + ".";
  const secondPart = introParts.slice(3, 6).join(". ") + ".";
  const thirdPart = introParts.slice(6).join(". ");

  return (
    <motion.section
      ref={ref}
      initial="hidden"
      animate={isVisible ? "visible" : "hidden"}
      variants={containerVariants}
      className="mb-12 md:mb-20 px-4 md:px-6"
    >
      <div className="max-w-5xl mx-auto">
        <div className="relative">
          {/* Decorative line */}
          {/* <motion.div
            initial={{ scaleY: 0 }}
            animate={isVisible ? { scaleY: 1 } : { scaleY: 0 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
            className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-primaryColor via-primaryColor/50 to-transparent hidden md:block"
            style={{ transformOrigin: "top" }}
          /> */}

          <div className=" space-y-6">
            <motion.div variants={textVariants} className="group">
              <motion.div
                whileHover={{ x: 5 }}
                className="bg-gradient-to-r from-secondaryColor/30 to-transparent p-6 rounded-lg border-l-4 border-primaryColor hover:shadow-lg transition-all duration-300"
              >
                <p className={cn("text-textSecondaryColor text-base md:text-lg leading-relaxed text-justify", normalInter)}>{firstPart}</p>
              </motion.div>
            </motion.div>

            <motion.div variants={textVariants} className="group">
              <motion.div
                whileHover={{ x: 5 }}
                className="bg-gradient-to-r from-secondaryColor/30 to-transparent p-6 rounded-lg border-l-4 border-primaryColor hover:shadow-lg transition-all duration-300"
              >
                <p className={cn("text-textSecondaryColor text-base md:text-lg leading-relaxed text-justify", normalInter)}>{secondPart}</p>
              </motion.div>
            </motion.div>

            <motion.div variants={textVariants} className="group">
              <motion.div
                whileHover={{ x: 5 }}
                className="bg-gradient-to-r from-secondaryColor/30 to-transparent p-6 rounded-lg border-l-4 border-primaryColor hover:shadow-lg transition-all duration-300"
              >
                <p className={cn("text-textSecondaryColor text-base md:text-lg leading-relaxed text-justify", normalInter)}>{thirdPart}</p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.section>
  );
};

// Image Showcase Section
const ImageShowcaseSection = () => {
  const [ref, isVisible] = useIntersectionObserver();
  const { normalInter } = useFontStyle();

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={isVisible ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className="mb-12 md:mb-20 px-4 md:px-6"
    >
      <div className="max-w-5xl mx-auto">
        <motion.div
          whileHover={{ scale: 1.02 }}
          className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-primaryColor/20 hover:border-primaryColor/50 transition-all duration-300 group"
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent z-10" />
          <motion.img
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.5 }}
            src={sampradayData.content.mainImage.src}
            alt={sampradayData.content.mainImage.alt}
            className="w-full aspect-video object-cover"
            loading="lazy"
          />
          <div className="absolute bottom-0 left-0 right-0 z-20 p-6 md:p-8">
            <p className={cn("text-white text-lg md:text-xl font-semibold drop-shadow-lg", normalInter)}>{sampradayData.content.mainImage.alt}</p>
          </div>
        </motion.div>
      </div>
    </motion.section>
  );
};

// Philosophy Section with Cards
const PhilosophySection = () => {
  const [ref, isVisible] = useIntersectionObserver();
  const { normalInter, medium } = useFontStyle();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.3,
      },
    },
  };

  const titleVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.9, rotateX: 10 },
    visible: {
      opacity: 1,
      scale: 1,
      rotateX: 0,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  return (
    <motion.section ref={ref} initial="hidden" animate={isVisible ? "visible" : "hidden"} className="mb-12 md:mb-20 px-4 md:px-6">
      <div className="max-w-5xl mx-auto">
        <motion.div variants={titleVariants} className="mb-10">
          <h2 className={cn("text-2xl md:text-3xl font-bold text-textColor mb-4 inline-block border-b-4 border-primaryColor pb-2", medium)}>
            Philosophy of Vishishtadvaita
          </h2>
          <p className={cn("text-textSecondaryColor text-base md:text-lg leading-relaxed text-justify mt-6", normalInter)}>
            {sampradayData.content.philosophyText}
          </p>
        </motion.div>

        {/* Philosophy Elements Cards */}
        <motion.div variants={containerVariants} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sampradayData.content.philosophyElements.map((item, index) => (
            <motion.div
              key={item.key}
              variants={cardVariants}
              whileHover={{
                y: -10,
                boxShadow: "0 20px 40px rgba(217, 126, 87, 0.25)",
              }}
              className="bg-gradient-to-br  flex flex-col justify-between from-white via-secondaryColor/20 to-white p-6 rounded-xl border-2 border-primaryColor/30 shadow-lg hover:border-primaryColor transition-all duration-300 group"
            >
              <div>
                {/* Number Badge */}
                <motion.div
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.6 }}
                  className="w-12 h-12 bg-gradient-to-br from-primaryColor to-primaryColor/70 rounded-full flex items-center justify-center mb-4 shadow-md"
                >
                  <span className={cn("text-white text-xl font-bold", medium)}>{index + 1}</span>
                </motion.div>

                {/* Title */}
                <h3 className={cn("text-xl font-bold text-primaryColor mb-3 group-hover:text-textColor transition-colors duration-300", medium)}>
                  {item.key}
                </h3>

                {/* Description */}
                <p className={cn("text-textSecondaryColor text-sm md:text-base leading-relaxed", normalInter)}>{item.value}</p>
              </div>

              {/* Decorative Bottom Line */}
              <motion.div
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                transition={{ delay: 0.3, duration: 0.5 }}
                className="mt-4 h-1 bg-gradient-to-r from-primaryColor to-transparent"
                style={{ transformOrigin: "left" }}
              />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </motion.section>
  );
};

// Conclusion Section
const ConclusionSection = () => {
  const [ref, isVisible] = useIntersectionObserver();
  const { normalInter, medium } = useFontStyle();

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.7, ease: "easeOut" }}
      className="mb-12 md:mb-20 px-4 md:px-6"
    >
      <div className="max-w-5xl mx-auto">
        <motion.div
          whileHover={{
            scale: 1.02,
            boxShadow: "0 25px 50px rgba(217, 126, 87, 0.2)",
          }}
          className="bg-gradient-to-br from-primaryColor/10 via-secondaryColor/30 to-primaryColor/10 rounded-2xl p-8 md:p-12 border-2 border-primaryColor/40 shadow-xl"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={isVisible ? { scale: 1 } : { scale: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="w-16 h-16 bg-primaryColor rounded-full flex items-center justify-center mb-6 mx-auto shadow-lg"
          >
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </motion.div>

          <h2 className={cn("text-2xl md:text-3xl font-bold text-textColor mb-6 text-center", medium)}>Conclusion</h2>

          <p className={cn("text-textSecondaryColor text-base md:text-lg leading-relaxed text-center", normalInter)}>
            {sampradayData.content.conclusion}
          </p>

          {/* Decorative elements */}
          <div className="flex justify-center gap-2 mt-8">
            {[...Array(3)].map((_, i) => (
              <motion.div
                key={i}
                initial={{ scale: 0 }}
                animate={isVisible ? { scale: 1 } : { scale: 0 }}
                transition={{ delay: 0.5 + i * 0.1, duration: 0.3 }}
                className="w-2 h-2 bg-primaryColor rounded-full"
              />
            ))}
          </div>
        </motion.div>
      </div>
    </motion.section>
  );
};

function Sampraday() {
  return (
    <div className="w-full bg-gradient-to-b from-white via-secondaryColor/5 to-white">
      <div className="w-full">
        <HeroSection />
        <IntroductionSection />
        <ImageShowcaseSection />
        <PhilosophySection />
        <ConclusionSection />
      </div>
    </div>
  );
}

export default Sampraday;
